import { addDays } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { Edit as EditIcon } from '@mui/icons-material'
import CarRepairIcon from '@mui/icons-material/CarRepair'
import { Box, Button, ButtonGroup, Divider, Link, Paper, Stack, Typography } from '@mui/material'
import { useCreateAutoRslaWorksheet } from '@src/data/api/auto-rsla-worksheet/auto-rsla-worksheet-api'
import { Constants, CreditApplication, ETermMax, User, VehicleSchema } from '@src/data/types'
import { AutoRslaWorksheet } from '@src/data/types/AutoRslaWorksheet'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface Props {
  creditApplication: CreditApplication
  worksheet: AutoRslaWorksheet
  user: User | null
  editDisabled: boolean
}

const AutoRslaWorksheetInformationSection = ({ worksheet, creditApplication, user, editDisabled }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [createWorksheet, _isCreatingWorksheet] = useCreateAutoRslaWorksheet()

  const editWorksheetDisabled =
    creditApplication?.originSystemId !== EOriginSystemId.Backoffice || !user?.rights.canEditWorksheet || editDisabled
  // event handlers
  const handleCreateWorksheet = useCallback(async () => {
    const route = `/Applications/auto-rsla/${creditApplication.id}/worksheet/edit`

    if (worksheet === null) {
      const newWorksheet = {
        id: creditApplication.id,
        status: EWorksheetStatus.Draft,
        vehicle: VehicleSchema.getDefault(),
        amountRequested: creditApplication.requestedLoanAmount,
        deliveryOn: formatDate(new Date()),
        firstPaymentOn: formatDate(addDays(new Date(), 30)),
        paymentFrequency: EPaymentFrequency.Monthly,
        paymentAmountForFrequency: 0,
        term: ETermMax.thirtySix,
        includeInsurance: false,
        versionTag: undefined,
        lienAmountFee: Constants.financingProgramConfigs[creditApplication.financingProgramId].lienAmountFee,
        brokerAdminFee: 0,
        roadsideAssistanceAmount: 0,
        merchantReferralFee: 0,
        paymentPlanId: EPaymentPlan.RegularDailyInterests,
        creditApplicationId: creditApplication.id,
        insuranceFee: 0,
      }
      try {
        await createWorksheet(newWorksheet)
        navigate(route)
      } catch (error) {
        reportErrorToConsole(error)
      }
    } else {
      navigate(route)
    }
  }, [worksheet, creditApplication, createWorksheet, navigate])
  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <ButtonGroup variant="outlined" size="large">
              <Button id="btnEditWorksheet" component={Link} variant="outlined" disabled>
                {t('worksheet.financing')}
              </Button>
              <Button onClick={handleCreateWorksheet} disabled={editWorksheetDisabled} size="small">
                <EditIcon fontSize="large" />
              </Button>
            </ButtonGroup>
            <CarRepairIcon fontSize="large" color="primary" />
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="top"
          marginTop={1}
          justifyContent="space-evenly"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.vin')} : {worksheet?.vehicle?.vin}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.vehicle')} : {worksheet?.vehicle?.make} {worksheet?.vehicle?.model}{' '}
              {worksheet?.vehicle?.series} {worksheet?.vehicle?.year}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.mileage')} : {worksheet?.vehicle?.mileage}{' '}
              {worksheet?.vehicle?.mileage !== undefined && 'KM'}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              CBB :
              {worksheet?.vehicle?.thirdPartyEvaluationValue
                ? formatCurrency(worksheet?.vehicle?.thirdPartyEvaluationValue)
                : 'N/A'}
            </Typography>
            <Typography variant="subtitle1">
              {t('worksheet.merchantEstimatedValue')} :{' '}
              {worksheet?.vehicle?.brokerEstimatedValue
                ? formatCurrency(worksheet?.vehicle?.brokerEstimatedValue)
                : 'N/A'}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentFrequency')} :{' '}
              {worksheet && t(`EPaymentFrequency.${worksheet?.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.term')} : {worksheet?.term} {worksheet?.term !== undefined && t('worksheet.month')}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentAmount')} : {formatCurrency(worksheet?.paymentAmountForFrequency)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.plan')} :{' '}
              {worksheet?.paymentPlanId ? t(`EPaymentPlan.${worksheet?.paymentPlanId}`, { ns: 'enum' }) : ''}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.deliveryOn')} : {formatDate(worksheet?.deliveryOn)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.firstPaymentOn')} : {formatDate(worksheet?.firstPaymentOn)}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheetCommon.amountRequested')} : {formatCurrency(worksheet?.amountRequested)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.brokerAdminFee')} : {formatCurrency(worksheet?.brokerAdminFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.ppsa')} : {formatCurrency(worksheet?.lienAmountFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.roadside')} : {formatCurrency(worksheet?.roadsideAssistanceAmount)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.merchantReferralFee')} : {formatCurrency(worksheet?.merchantReferralFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.protectionPlan')} : {formatCurrency(worksheet?.insuranceFee)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.totalAmountFinanced')} : {formatCurrency(worksheet?.totalAmountFinanced)}
            </Typography>
          </span>
        </Stack>
      </Box>
    </Paper>
  )
}

export default AutoRslaWorksheetInformationSection
